import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { message, Modal } from "antd";
import { Trash2 } from "lucide-react";
import {
  Accounts_Query,
  Get_All_Job_Role,
  update_Account,
  Role_Management,
  Delete_Job_Role,
  Upload_Document,
} from "../../services/userAPI";
import { COUNTRIES, STATES_BY_COUNTRY, CITIES_BY_STATE } from "./constants";
import {
  FieldLabel,
  TextField,
  CustomSelect,
  PhoneField,
  LogoUpload,
  RadioOption,
  SectionCard,
} from "./formFields";
import BackArrow from "../../Images/backArrowLiteAndGrowthImage.png";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parsePhone = (raw = "") => {
  const match = raw.trim().match(/^(\+\d+)\s*(\d*)$/);
  if (!match) return { countryCode: "IN", mobile: raw.replace(/\D/g, "").slice(0, 10) };
  const dial = match[1];
  const country = COUNTRIES.find((c) => c.dialCode === dial);
  return { countryCode: country ? country.code : "IN", mobile: (match[2] || "").slice(0, 10) };
};

const dialCodeFor = (code) => COUNTRIES.find((c) => c.code === code)?.dialCode || "+91";

const EditClientAndRole = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const accountId = parseInt(id);
  const passedRole = location.state?.role;
  // Role-only modes (dashboard pencil edit / Add Role button): hide Company
  // Details, show only SPOC + Clan Role Configuration. addRoleOnly starts blank.
  const addRoleOnly = Boolean(location.state?.addRoleOnly);
  const isRoleEdit = Boolean(passedRole) || addRoleOnly;

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadedFilename, setUploadedFilename] = useState(null);
  const [company, setCompany] = useState({
    companyName: "",
    companyAddress: "",
    city: "",
    state: "",
    country: "",
  });
  const [spoc, setSpoc] = useState({ name: "", email: "", countryCode: "IN", mobile: "", location: "" });
  const [roleForm, setRoleForm] = useState({
    id: null,
    roleName: "",
    pocName: "",
    countryCode: "IN",
    mobile: "",
    email: "",
    loginMethod: "email",
  });
  const [accountMeta, setAccountMeta] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  /* ------------------------------- data fetch ------------------------------ */
  const { data: accountsData, loading: accountsLoading } = useQuery(Accounts_Query, {
    fetchPolicy: "network-only",
  });
  const { data: rolesData, loading: rolesLoading } = useQuery(Get_All_Job_Role, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: "network-only",
  });

  const loading = accountsLoading || rolesLoading;

  useEffect(() => {
    if (loaded) return;
    const accounts = accountsData?.allUserAccount;
    const roles = rolesData?.getAllJobrole;
    if (!accounts || !roles) return;

    const account = accounts.find((a) => parseInt(a.id) === accountId);
    if (account) {
      setAccountMeta(account);
      let city = "", state = "", country = "";
      if (account.address2) {
        const parts = account.address2.split(",").map((s) => s.trim());
        [city, state, country] = parts;
      }
      if (!country) country = "India";
      setCompany({
        companyName: account.name || "",
        companyAddress: account.address || "",
        city: city || "",
        state: state || "",
        country,
      });
      if (account.mainLogoUrl) setLogoPreview(account.mainLogoUrl);
    }

    const role = passedRole
      ? roles.find((r) => parseInt(r.id) === parseInt(passedRole.id)) || roles[0]
      : roles[0];

    if (addRoleOnly) {
      // New role — leave role fields blank, prefill SPOC from the account's POC
      if (role) {
        const parsed = parsePhone(role.pocContactNumber || role.pocNumber || "");
        setSpoc({
          name: role.pocName || "",
          email: role.pocEmail || "",
          countryCode: parsed.countryCode,
          mobile: parsed.mobile,
          location: "",
        });
      }
    } else if (role) {
      const parsed = parsePhone(role.pocContactNumber || role.pocNumber || "");
      setRoleForm({
        id: role.id,
        roleName: role.name || "",
        pocName: role.pocName || "",
        countryCode: parsed.countryCode,
        mobile: parsed.mobile,
        email: role.pocEmail || "",
        loginMethod: role.emailLogin ? "email" : role.numberLogin ? "mobile" : "email",
      });
      setSpoc((prev) => ({
        ...prev,
        name: prev.name || role.pocName || "",
        email: prev.email || role.pocEmail || "",
        countryCode: parsed.countryCode,
        mobile: parsed.mobile,
      }));
    }
    setLoaded(true);
  }, [accountsData, rolesData, accountId, passedRole, addRoleOnly, loaded]);

  /* ------------------------------- mutations ------------------------------- */
  const [uploadDocument] = useMutation(Upload_Document);
  const [updateClient] = useMutation(update_Account);
  const [addJobrole] = useMutation(Role_Management);
  const [deleteJobrole] = useMutation(Delete_Job_Role);

  /* ------------------------------- validation ------------------------------ */
  const validate = () => {
    const errs = {};
    if (!isRoleEdit) {
      if (!company.companyName.trim()) errs.companyName = "Company Name is required";
      if (!company.companyAddress.trim()) errs.companyAddress = "Company Address is required";
      if (!company.country) errs.country = "Country is required";
      if (!company.state) errs.state = "State is required";
      if (!company.city) errs.city = "City is required";
    }
    if (!spoc.name.trim()) errs.spocName = "SPOC Name is required";
    if (!spoc.email.trim()) errs.spocEmail = "Official Email ID is required";
    else if (!emailPattern.test(spoc.email.trim())) errs.spocEmail = "Enter a valid Email ID";
    if (!spoc.mobile) errs.spocMobile = "Mobile Number is required";
    else if (!/^\d{10}$/.test(spoc.mobile)) errs.spocMobile = "Enter a valid 10-digit mobile number";
    if (!spoc.location.trim()) errs.spocLocation = "SPOC Location is required";
    if (!roleForm.roleName.trim()) errs.roleName = "Role Name is required";
    if (!roleForm.pocName.trim()) errs.pocName = "POC Name is required";
    if (!roleForm.mobile) errs.roleMobile = "Mobile Number is required";
    else if (!/^\d{10}$/.test(roleForm.mobile)) errs.roleMobile = "Enter a valid 10-digit mobile number";
    if (!roleForm.email.trim()) errs.roleEmail = "Email-ID is required";
    else if (!emailPattern.test(roleForm.email.trim())) errs.roleEmail = "Enter a valid Email-ID";
    return errs;
  };

  /* --------------------------------- actions ------------------------------- */
  const handleDeleteRole = () => {
    if (!roleForm.id) {
      message.error("No role selected to delete");
      return;
    }
    Modal.confirm({
      title: "Delete Role",
      content: `Are you sure you want to delete the role "${roleForm.roleName}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await deleteJobrole({ variables: { jobroleId: parseInt(roleForm.id) } });
          if (res?.data?.deleteJobrole?.success) {
            message.success("Role deleted successfully");
            navigate("/clan-lite-growth-dashboard");
          } else {
            message.error(res?.data?.deleteJobrole?.message || "Failed to delete role");
          }
        } catch (err) {
          message.error("Something went wrong");
        }
      },
    });
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      message.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      // Role-edit mode only updates the role — company fields are hidden.
      if (!isRoleEdit) {
        let logoName = uploadedFilename || null;
        if (logoFile) {
          const res = await uploadDocument({
            variables: { s3folder: "COMPANY_LOGO", document: logoFile },
            context: { useMultipart: true },
          });
          if (res?.data?.uploadDocument?.success) {
            logoName = res.data.uploadDocument?.data?.data?.filename || null;
            setUploadedFilename(logoName);
          }
        }

        const brandColor = (accountMeta?.brandColor || "E11D2E").replace("#", "");
        const res = await updateClient({
          variables: {
            accountId,
            name: company.companyName.trim(),
            address: company.companyAddress.trim(),
            address2: `${company.city}, ${company.state}, ${company.country}`,
            url: accountMeta?.url || company.companyName.toLowerCase().replace(/[^a-z0-9]+/g, ""),
            mainLogo: logoName || accountMeta?.mainLogoUrl || undefined,
            smallLogo: logoName || accountMeta?.smallLogoUrl || accountMeta?.mainLogoUrl || undefined,
            emailLogin: roleForm.loginMethod === "email",
            numberLogin: roleForm.loginMethod === "mobile",
            brandColor,
            selfRegistration: accountMeta?.selfRegistration || false,
            assignedUsersForClient: [],
          },
        });

        if (!res?.data?.updateAccount?.success) {
          message.error(res?.data?.updateAccount?.message || "Failed to update client");
          return;
        }
      }

      if (roleForm.id || addRoleOnly) {
        const roleRes = await addJobrole({
          variables: {
            accountId,
            name: roleForm.roleName.trim(),
            clanNeeded: true,
            syncNeeded: false,
            dartsNeeded: false,
            dartsFormNeeded: false,
            jobroleId: roleForm.id ? parseInt(roleForm.id) : null,
            pocName: roleForm.pocName.trim(),
            pocEmail: roleForm.email.trim(),
            pocNumber: `${dialCodeFor(roleForm.countryCode)} ${roleForm.mobile}`,
            geoTracking: false,
            numberLogin: roleForm.loginMethod === "mobile",
            emailLogin: roleForm.loginMethod === "email",
            mandatoryTimeDistribution: false,
            signInSignOutRequired: false,
            locationRequired: false,
            targetEditDays: 0,
            inputEditDays: 0,
            jobroleAccessUserIds: [],
            ldmsNeeded: false,
            mirrorNeeded: false,
          },
        });
        if (!roleRes?.data?.addJobrole?.success) {
          message.error(roleRes?.data?.addJobrole?.message || "Client updated but role update failed");
          return;
        }
      }

      message.success(
        addRoleOnly
          ? "Role created successfully!"
          : isRoleEdit
            ? "Role updated successfully!"
            : "Client updated successfully!"
      );
      navigate("/clan-lite-growth-dashboard");
    } catch (err) {
      console.error(err);
      const gqlError = err?.graphQLErrors?.[0]?.message || err.message;
      message.error(gqlError || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(-1);

  /* -------------------------------- derived -------------------------------- */
  const countryOptions = useMemo(() => COUNTRIES.map((c) => c.name), []);
  const selectedCountryCode = COUNTRIES.find((c) => c.name === company.country)?.code;
  const stateOptions = selectedCountryCode ? STATES_BY_COUNTRY[selectedCountryCode] || [] : [];
  const cityOptions = company.state ? CITIES_BY_STATE[company.state] || [] : [];

  if (loading && !loaded) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="h-10 w-10 border-4 border-[#E11D2E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 pt-[88px] pb-10">
      <div className="mx-auto">
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center gap-1.5 text-[#AD0101] mb-2"
        >
          <img src={BackArrow} alt="Back" className="h-3.5 w-3.5 object-contain" />
          <span className="text-[12px] leading-5 font-semibold">Back</span>
        </button>
        <h1 className="text-[20px] leading-5 font-semibold text-[#1F2020] mb-4">
          {addRoleOnly ? "Add Role" : "Edit Client and role"}
        </h1>

        {/* 1. Company Details — hidden in role-edit mode */}
        {!isRoleEdit && (
        <SectionCard number={1} title="Company Details" right={<span className="flex-1" />}>
          <div className="flex gap-6">
            <div>
              <FieldLabel>Company Logo</FieldLabel>
              <div className="">
              <LogoUpload
                preview={logoPreview}
                onFileSelect={(file) => {
                  if (!["image/jpeg", "image/png"].includes(file.type)) {
                    message.error("Only JPEG and PNG images are allowed");
                    return;
                  }
                  setLogoFile(file);
                  if (logoPreview && logoFile) URL.revokeObjectURL(logoPreview);
                  setLogoPreview(URL.createObjectURL(file));
                }}
              />
              </div>
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-x-5">
                <TextField
                  label="Company Name"
                  required
                  placeholder="Enter Company name"
                  value={company.companyName}
                  error={errors.companyName}
                  onChange={(v) => {
                    setCompany((p) => ({ ...p, companyName: v }));
                    if (errors.companyName) setErrors((p) => ({ ...p, companyName: undefined }));
                  }}
                />
                <TextField
                  label="Company Address"
                  required
                  placeholder="Enter full address"
                  value={company.companyAddress}
                  error={errors.companyAddress}
                  onChange={(v) => {
                    setCompany((p) => ({ ...p, companyAddress: v }));
                    if (errors.companyAddress) setErrors((p) => ({ ...p, companyAddress: undefined }));
                  }}
                />
              </div>
              <div className="grid grid-cols-3 gap-x-5 mt-4">
                <CustomSelect
                  label="Company Headquarters(City)"
                  required
                  placeholder="Select City"
                  value={company.city}
                  options={cityOptions}
                  error={errors.city}
                  onChange={(v) => {
                    setCompany((p) => ({ ...p, city: v }));
                    if (errors.city) setErrors((p) => ({ ...p, city: undefined }));
                  }}
                />
                <CustomSelect
                  label="Company Headquarters(State)"
                  required
                  placeholder="Select State"
                  value={company.state}
                  options={stateOptions}
                  error={errors.state}
                  onChange={(v) => {
                    setCompany((p) => ({ ...p, state: v, city: "" }));
                    if (errors.state) setErrors((p) => ({ ...p, state: undefined }));
                  }}
                />
                <CustomSelect
                  label="Company Headquarters(Country)"
                  required
                  placeholder="Select Country"
                  value={company.country}
                  options={countryOptions}
                  error={errors.country}
                  onChange={(v) => {
                    setCompany((p) => ({ ...p, country: v, state: "", city: "" }));
                    if (errors.country) setErrors((p) => ({ ...p, country: undefined }));
                  }}
                />
              </div>
            </div>
          </div>
        </SectionCard>
        )}

        {/* SPOC Details */}
        <SectionCard number={isRoleEdit ? 1 : 2} title="SPOC Details" right={<span className="flex-1" />}>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">
            <TextField
              label="SPOC Name"
              required
              placeholder="Enter SPOC Name"
              value={spoc.name}
              error={errors.spocName}
              onChange={(v) => {
                setSpoc((p) => ({ ...p, name: v }));
                if (errors.spocName) setErrors((p) => ({ ...p, spocName: undefined }));
              }}
            />
            <TextField
              label="Official Email ID"
              required
              placeholder="Enter Email Id"
              value={spoc.email}
              error={errors.spocEmail}
              onChange={(v) => {
                setSpoc((p) => ({ ...p, email: v }));
                setErrors((p) => ({
                  ...p,
                  spocEmail: v.trim() && !emailPattern.test(v.trim())
                    ? "Please enter a valid Email ID"
                    : undefined,
                }));
              }}
            />
            <PhoneField
              label="Mobile Number"
              required
              value={spoc.mobile}
              countryCode={spoc.countryCode}
              error={errors.spocMobile}
              onCountryChange={(c) => setSpoc((p) => ({ ...p, countryCode: c }))}
              onChange={(v) => {
                setSpoc((p) => ({ ...p, mobile: v }));
                setErrors((p) => ({
                  ...p,
                  spocMobile: v && !/^\d{10}$/.test(v)
                    ? "Mobile number must be exactly 10 digits"
                    : undefined,
                }));
              }}
            />
            <TextField
              label="SPOC Location"
              required
              placeholder="Enter SPOC Location"
              value={spoc.location}
              error={errors.spocLocation}
              onChange={(v) => {
                setSpoc((p) => ({ ...p, location: v }));
                if (errors.spocLocation) setErrors((p) => ({ ...p, spocLocation: undefined }));
              }}
            />
          </div>
        </SectionCard>

        {/* Clan Role Configuration */}
        <SectionCard number={isRoleEdit ? 2 : 3} title="Clan Role Configuration" right={<span className="flex-1" />}>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4">
            <TextField
              label="Role Name"
              required
              placeholder="Enter Role Name"
              value={roleForm.roleName}
              error={errors.roleName}
              onChange={(v) => {
                setRoleForm((p) => ({ ...p, roleName: v }));
                if (errors.roleName) setErrors((p) => ({ ...p, roleName: undefined }));
              }}
            />
            <TextField
              label="POC Name"
              required
              placeholder="Enter POC Name"
              value={roleForm.pocName}
              error={errors.pocName}
              onChange={(v) => {
                setRoleForm((p) => ({ ...p, pocName: v }));
                if (errors.pocName) setErrors((p) => ({ ...p, pocName: undefined }));
              }}
            />
            <PhoneField
              label="Mobile Number"
              required
              value={roleForm.mobile}
              countryCode={roleForm.countryCode}
              error={errors.roleMobile}
              onCountryChange={(c) => setRoleForm((p) => ({ ...p, countryCode: c }))}
              onChange={(v) => {
                setRoleForm((p) => ({ ...p, mobile: v }));
                setErrors((p) => ({
                  ...p,
                  roleMobile: v && !/^\d{10}$/.test(v)
                    ? "Mobile number must be exactly 10 digits"
                    : undefined,
                }));
              }}
            />
            <TextField
              label="Email-ID"
              required
              placeholder="Enter Email ID"
              value={roleForm.email}
              error={errors.roleEmail}
              onChange={(v) => {
                setRoleForm((p) => ({ ...p, email: v }));
                setErrors((p) => ({
                  ...p,
                  roleEmail: v.trim() && !emailPattern.test(v.trim())
                    ? "Please enter a valid Email-ID"
                    : undefined,
                }));
              }}
            />
          </div>
          <div className="mt-4">
            <label className="block text-[20px] font-semibold leading-7 text-[#1F2020] mb-2">
              Login through <span className="text-[#AD0101] ml-0.5">*</span>
            </label>
            <div className="flex items-center gap-6 h-[42px]">
              <RadioOption
                label="Email-ID"
                checked={roleForm.loginMethod === "email"}
                onChange={() => setRoleForm((p) => ({ ...p, loginMethod: "email" }))}
              />
              <RadioOption
                label="Mobile no."
                checked={roleForm.loginMethod === "mobile"}
                onChange={() => setRoleForm((p) => ({ ...p, loginMethod: "mobile" }))}
              />
            </div>
          </div>
        </SectionCard>

        {/* Actions */}
        <div className="flex items-center justify-between pb-10">
          {!addRoleOnly ? (
            <button
              type="button"
              onClick={handleDeleteRole}
              className="h-12 px-6 rounded border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020] hover:border-[#AD0101] hover:text-[#AD0101] transition flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete Role
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="h-12 w-[100px] rounded border border-[#D9DDE3] bg-white text-[14px] font-medium text-[#1F2020] hover:border-[#AD0101] hover:text-[#AD0101] transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className={`h-12 min-w-[88px] px-[18px] rounded text-[14px] font-medium text-white transition
                ${submitting ? "bg-[#E47777] cursor-not-allowed" : "bg-[#AD0101] hover:bg-[#8C0101]"}`}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Full-screen loader: holds the screen until all APIs complete */}
      {submitting && (
        <div
          className="fixed inset-0 z-[9999] bg-white/70 backdrop-blur-[2px] flex flex-col items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-12 w-12 border-4 border-[#CF142B] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-[14px] font-medium text-[#1F2937]">{isRoleEdit ? "Saving role..." : "Saving client and role..."}</p>
          <p className="mt-1 text-[12px] text-gray-500">Please wait, do not close this screen</p>
        </div>
      )}
    </div>
  );
};

export default EditClientAndRole;
